<template>
  <div id="reportPage">
    <div v-if="isLoading" class="loading-overlay">
      <div class="loading-box">
        <div class="loading-spinner"></div>
        <div class="loading-text">Loading...</div>
      </div>
    </div>
    <main>
      <article>
        <div class="title-wrapper mb-4">
          <h2 class="login-title">REFUND REQUESTS</h2>
          <UserMenu :roles="roles" :user-name="userName" />
        </div>

        <div class="table-top-filters d-flex justify-content-end mb-2" style="width: 100%;">
          <input
            type="text"
            class="form-control form-control-sm daterange datepicker-top"
            placeholder="Select date range"
            readonly
          />
          <a
            href="#"
            class="link-csv"
            :class="{ 'link-csv--disabled': isExportingCsv }"
            @click.prevent="downloadCSV"
          >
            <font-awesome-icon icon="file-csv" />
            {{ isExportingCsv ? 'Preparing CSV…' : 'Download CSV' }}
          </a>
        </div>

        <div class="table-responsive">
          <table class="table table-bordered align-middle">
            <thead class="table-light">
              <tr>
                <th></th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>INBOUND NUMBER</span>
                    <button
                      type="button"
                      ref="inboundPhoneFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: isFilterActive('inbound_phone') }"
                      @click.stop="toggleTextFilterPopover('inbound_phone', 'Filter by Inbound Number', 'inboundPhoneFilterBtn')"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="isFilterActive('inbound_phone')" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>CALL DATE</span>
                    <button
                      type="button"
                      ref="callDateFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: isFilterActive('call_dt') }"
                      @click.stop="toggleTextFilterPopover('call_dt', 'Filter by Call Date', 'callDateFilterBtn')"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="isFilterActive('call_dt')" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>CALL DURATION</span>
                    <button
                      type="button"
                      ref="callLengthFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: isFilterActive('call_length') }"
                      @click.stop="toggleTextFilterPopover('call_length', 'Filter by Call Duration', 'callLengthFilterBtn')"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="isFilterActive('call_length')" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>HANGUP</span>
                    <button
                      type="button"
                      ref="hangupFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: isFilterActive('end_call_source') }"
                      @click.stop="toggleTextFilterPopover('end_call_source', 'Filter by Hangup', 'hangupFilterBtn')"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="isFilterActive('end_call_source')" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>TARGET NAME</span>
                    <button
                      type="button"
                      ref="targetNameFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: isFilterActive('target_name') }"
                      @click.stop="toggleTextFilterPopover('target_name', 'Filter by Target Name', 'targetNameFilterBtn')"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="isFilterActive('target_name')" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>TARGET NUMBER</span>
                    <button
                      type="button"
                      ref="targetNumberFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: isFilterActive('target_number') }"
                      @click.stop="toggleTextFilterPopover('target_number', 'Filter by Target Number', 'targetNumberFilterBtn')"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="isFilterActive('target_number')" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>REASON</span>
                    <button
                      type="button"
                      ref="reasonFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: isFilterActive('reason') }"
                      @click.stop="toggleTextFilterPopover('reason', 'Filter by Reason', 'reasonFilterBtn')"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="isFilterActive('reason')" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>REFUND DECISION</span>
                    <button
                      type="button"
                      ref="statusFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: isFilterActive('is_applied') }"
                      @click.stop="toggleStatusFilterPopover"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="isFilterActive('is_applied')" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>CREATED DATE</span>
                    <button
                      type="button"
                      ref="createdDateFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: isFilterActive('created_date') }"
                      @click.stop="toggleTextFilterPopover('created_date', 'Filter by Created Date', 'createdDateFilterBtn')"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="isFilterActive('created_date')" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
                <th class="campaign-header-cell">
                  <div class="campaign-header-cell-inner">
                    <span>CLIENT</span>
                    <button
                      type="button"
                      ref="userFilterBtn"
                      class="filter-icon-btn"
                      :class="{ active: isFilterActive('user') }"
                      @click.stop="toggleTextFilterPopover('user', 'Filter by Client', 'userFilterBtn')"
                    >
                      <font-awesome-icon icon="filter" />
                      <span v-if="isFilterActive('user')" class="filter-badge">1</span>
                    </button>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, i) in rows" :key="i">
                <td>
                  <a href="#" class="request-id-link" @click.prevent="openRefundModal(row)">Request #{{ row.id }}</a>
                </td>
                <td>{{ row.inbound_phone }}</td>
                <td>{{ row.call_dt }}</td>
                <td>{{ row.call_length }}</td>
                <td class="text-center">
                  <template v-if="row.end_call_source">
                    <span v-if="row.end_call_source === 'Target'" class="tooltip-container">
                      <font-awesome-icon :icon="['fas', 'location-crosshairs']" />
                      <span class="tooltip-text right">Target</span>
                    </span>
                    <span v-else-if="row.end_call_source === 'System'" class="tooltip-container">
                      <font-awesome-icon :icon="['fas', 'computer']" />
                      <span class="tooltip-text right">System</span>
                    </span>
                    <span v-else-if="row.end_call_source === 'Caller'" class="tooltip-container">
                      <font-awesome-icon :icon="['fas', 'mobile-screen']" />
                      <span class="tooltip-text right">Caller</span>
                    </span>
                    <span v-else>{{ row.end_call_source }}</span>
                  </template>
                </td>
                <td>{{ row.target_name }}</td>
                <td>{{ row.target_number }}</td>
                <td>
                  <span
                    class="refund-reason-chip refund-reason-chip-clickable"
                    :class="{
                      'refund-chip-approved': Number(row.is_applied) === 1,
                      'refund-chip-rejected': Number(row.is_applied) === 2,
                      'refund-chip-pending': Number(row.is_applied) === 0 || row.is_applied == null
                    }"
                    @click.stop="showReasonOnly(row.reason)"
                    :title="row.reason ? 'Click to view full reason' : ''"
                  >
                    <span class="refund-chip-status-dot"></span>
                    <span class="refund-chip-text">{{ row.reason || '—' }}</span>
                  </span>
                </td>
                <td class="text-center">
                  <!-- Для админов: показываем select только если редактируется эта строка, иначе badge -->
                  <select
                    v-if="roles.includes('admin') && editingStatusId === row.id"
                    v-model.number="row.is_applied"
                    @change="updateRefundStatus(row)"
                    @blur="editingStatusId = null"
                    class="form-select form-select-sm"
                    style="min-width: 120px;"
                  >
                    <option :value="0">n/a</option>
                    <option :value="1">Approved</option>
                    <option :value="2">Rejected</option>
                  </select>

                  <!-- Badge для всех (админы могут кликнуть для редактирования) -->
                  <span
                    v-else
                    class="status-badge"
                    :class="{
                      'approved': Number(row.is_applied) === 1,
                      'rejected': Number(row.is_applied) === 2,
                      'na': Number(row.is_applied) === 0 || row.is_applied == null,
                      'clickable': roles.includes('admin')
                    }"
                    @click="roles.includes('admin') && (editingStatusId = row.id)"
                    :style="roles.includes('admin') ? 'cursor: pointer;' : ''"
                  >
                    <span class="dot"></span>
                    {{ Number(row.is_applied) === 1 ? 'Approved' : Number(row.is_applied) === 2 ? 'Rejected' : 'n/a' }}
                  </span>
                </td>
                <td>{{ row.created_at }}</td>
                <td>{{ row.username }}</td>
              </tr>
              <tr v-if="rows.length === 0">
                <td colspan="11" class="text-center py-3">No results</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="d-flex justify-content-center align-items-center gap-2 mt-3">
          <button
            class="btn btn-sm btn-outline-primary"
            :disabled="currentPage === 1"
            @click="changePage(currentPage - 1)"
          >
            ◀ Prev
          </button>

          <span>
            Page {{ currentPage }} of {{ totalPages }}
          </span>

          <button
            class="btn btn-sm btn-outline-primary"
            :disabled="currentPage >= totalPages"
            @click="changePage(currentPage + 1)"
          >
            Next ▶
          </button>
        </div>

        <button class="btn-submit mt-3" @click="clearFilters">Clear filters</button>

        <div
          v-if="showTextFilterModal"
          ref="textFilterPopover"
          class="filter-popover caller-filter-popover"
          :style="textFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">{{ activeTextFilterTitle }}</span>
            <button type="button" class="filter-popover-close" @click="closeTextFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <input
              v-model="textFilterTemp"
              type="text"
              class="filter-popover-search"
              placeholder="Enter value"
              @keyup.enter="applyTextFilter"
            />
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearTextFilter">Clear</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyTextFilter">Apply</button>
          </div>
        </div>

        <div
          v-if="showStatusFilterModal"
          ref="statusFilterPopover"
          class="filter-popover"
          :style="statusFilterPopoverStyle"
        >
          <div class="filter-popover-header">
            <span class="filter-popover-title">Filter by Refund Decision</span>
            <button type="button" class="filter-popover-close" @click="closeStatusFilterModal" aria-label="Close">×</button>
          </div>
          <div class="filter-popover-body">
            <label
              v-for="option in refundDecisionOptions"
              :key="option.value"
              class="filter-popover-item"
            >
              <input v-model="statusFilterTemp" type="radio" :value="option.value" />
              <span>{{ option.label }}</span>
            </label>
          </div>
          <div class="filter-popover-footer">
            <button type="button" class="btn btn-sm btn-outline-secondary" @click="clearStatusFilter">Clear</button>
            <button type="button" class="btn btn-sm btn-primary" @click="applyStatusFilter">Apply</button>
          </div>
        </div>
      </article>
      <!-- Reason-only modal (click on reason in table) -->
      <div v-if="showReasonPopover" class="modal-overday" @click="closeReasonPopover">
        <div class="reason-modal-box" @click.stop>
          <div class="reason-modal-header">
            <span class="reason-modal-title">Return reason</span>
            <button type="button" class="reason-modal-close" @click="closeReasonPopover" aria-label="Close">×</button>
          </div>
          <div class="reason-modal-body">
            <p class="reason-modal-text">{{ reasonPopoverText || '—' }}</p>
          </div>
          <div class="reason-modal-footer">
            <button type="button" class="btn btn-secondary" @click="closeReasonPopover">Close</button>
          </div>
        </div>
      </div>
      <!-- Refund View Modal -->
      <div v-if="showRefundModal" class="modal-overday" @click="closeRefundModal">
        <div class="refund-modal-content" @click.stop>

          <div class="refund-modal-header">
            <div class="refund-modal-title-block">
              <span class="refund-modal-id">Request #{{ selectedRefundRow.id }}</span>
              <span
                class="status-badge ms-3"
                :class="{
                  'approved': Number(selectedRefundRow.is_applied) === 1,
                  'rejected': Number(selectedRefundRow.is_applied) === 2,
                  'na':  Number(selectedRefundRow.is_applied) === 0 || selectedRefundRow.is_applied == null
                }"
              >
                <span class="dot"></span>
                {{ Number(selectedRefundRow.is_applied) === 1 ? 'Approved' : Number(selectedRefundRow.is_applied) === 2 ? 'Rejected' : 'n/a' }}
              </span>
            </div>
            <button class="refund-modal-close" @click="closeRefundModal">✕</button>
          </div>

          <div class="refund-modal-body">
            <div class="refund-info-grid">
              <div class="refund-info-item">
                <span class="refund-info-label">Inbound Number</span>
                <span class="refund-info-value">{{ selectedRefundRow.inbound_phone || '—' }}</span>
              </div>
              <div class="refund-info-item">
                <span class="refund-info-label">Call Date</span>
                <span class="refund-info-value">{{ selectedRefundRow.call_dt || '—' }}</span>
              </div>
              <div class="refund-info-item">
                <span class="refund-info-label">Duration</span>
                <span class="refund-info-value">{{ selectedRefundRow.call_length ? selectedRefundRow.call_length + ' sec' : '—' }}</span>
              </div>
              <div class="refund-info-item">
                <span class="refund-info-label">Hangup</span>
                <span class="refund-info-value">
                  <template v-if="selectedRefundRow.end_call_source">
                    <span v-if="selectedRefundRow.end_call_source === 'Target'" class="tooltip-container">
                      <font-awesome-icon :icon="['fas', 'location-crosshairs']" />
                      <span class="tooltip-text right">Target</span>
                    </span>
                    <span v-else-if="selectedRefundRow.end_call_source === 'System'" class="tooltip-container">
                      <font-awesome-icon :icon="['fas', 'computer']" />
                      <span class="tooltip-text right">System</span>
                    </span>
                    <span v-else-if="selectedRefundRow.end_call_source === 'Caller'" class="tooltip-container">
                      <font-awesome-icon :icon="['fas', 'mobile-screen']" />
                      <span class="tooltip-text right">Caller</span>
                    </span>
                    <span v-else>{{ selectedRefundRow.end_call_source }}</span>
                  </template>
                  <span v-else>—</span>
                </span>
              </div>
              <div class="refund-info-item">
                <span class="refund-info-label">Target Name</span>
                <span class="refund-info-value">{{ selectedRefundRow.target_name || '—' }}</span>
              </div>
              <div class="refund-info-item">
                <span class="refund-info-label">Target Number</span>
                <span class="refund-info-value">{{ selectedRefundRow.target_number || '—' }}</span>
              </div>
              <div class="refund-info-item">
                <span class="refund-info-label">Client</span>
                <span class="refund-info-value">{{ selectedRefundRow.username || '—' }}</span>
              </div>
              <div class="refund-info-item">
                <span class="refund-info-label">Created At</span>
                <span class="refund-info-value">{{ selectedRefundRow.created_at || '—' }}</span>
              </div>
            </div>

            <div class="refund-reason-block refund-reason-block-full">
              <span class="refund-info-label">Refund Reason</span>
              <p class="refund-reason-full-text">{{ selectedRefundRow.reason || '—' }}</p>
            </div>

            <!-- Recording -->
            <div v-if="selectedRefundRow.recording_url" class="refund-recording-block">
              <span class="refund-info-label">Recording</span>
              <div class="refund-recording-controls">
                <button class="audio-btn play-btn" @click="toggleModalAudio">
                  <font-awesome-icon v-if="!modalAudioPlaying" icon="play" />
                  <font-awesome-icon v-else icon="pause" />
                </button>
                <audio
                  id="modal-audio"
                  :src="selectedRefundRow.recording_url"
                  preload="none"
                  @ended="modalAudioPlaying = false"
                  @error="modalAudioPlaying = false"
                  style="display: none;"
                ></audio>
                <div class="refund-recording-url">{{ truncateUrl(selectedRefundRow.recording_url) }}</div>
                <a class="audio-btn download-btn ms-2" @click.prevent="downloadModalRecording">
                  <font-awesome-icon icon="download" />
                </a>
              </div>
            </div>
          </div>

          <!-- Decision block (admin only) -->
          <div v-if="roles.includes('admin') && (Number(selectedRefundRow.is_applied) === 0 || selectedRefundRow.is_applied == null)" class="refund-modal-decision">
            <span class="refund-info-label mb-2">Make a decision</span>
            <div class="refund-decision-buttons">
              <button class="btn-decision approve" @click="makeDecision(1)">
                <font-awesome-icon icon="check" /> Approve
              </button>
              <button class="btn-decision reject" @click="makeDecision(2)">
                <font-awesome-icon icon="times" /> Reject
              </button>
            </div>
          </div>

          <div class="refund-modal-footer">
            <button class="btn btn-secondary" @click="closeRefundModal">Close</button>
          </div>

        </div>
      </div>
    </main>
  </div>
</template>

<script>
import UserMenu from '@/components/UserMenu.vue'
export default {
  components: { UserMenu },
  data() {
    return {
      rows: [],
      summary: {},
      campaignOptions: [],
      totalItems: 0,
      totalPages: 1,
      showRefundModal: false,
      selectedRefund: null,
      selectedRefundRow: {},
      modalAudioPlaying: false,
      showReasonPopover: false,
      reasonPopoverText: '',
      currentPage: 1,
      pageSize: 100,
      isLoading: false,
      isExportingCsv: false,
      userName: localStorage.getItem("username") || "User",
      roles: [],
      editingStatusId: null, // ID строки, в которой редактируется статус
      showTextFilterModal: false,
      activeTextFilterField: '',
      activeTextFilterTitle: '',
      activeTextFilterButtonRef: '',
      textFilterTemp: '',
      textFilterPopoverStyle: {},
      textFilterClickOutside: null,
      showStatusFilterModal: false,
      statusFilterTemp: '',
      statusFilterPopoverStyle: {},
      statusFilterClickOutside: null,
      refundDecisionOptions: [
        { value: '0', label: 'n/a' },
        { value: '1', label: 'Approved' },
        { value: '2', label: 'Rejected' }
      ],
      filters: {
        inbound_phone: "",
        call_dt: "",
        call_length: "",
        end_call_source: "",
        target_name: "",
        target_number: "",
        reason: "",
        is_applied: "",
        created_date: "",
        user: "",
        date_from: "",
        date_to: ""
      }
    };
  },
  computed: {
  },
  mounted() {
    const token = localStorage.getItem('token');
    if (!token) {
      window.location.href = '/login';
      return;
    }

    this.fetchData();
    const idParam = this.$route && this.$route.query && this.$route.query.id;
    if (idParam) {
      this.openRefundById(Number(idParam));
    }
    this.check_role();
    document.title = "Refund Requests";

    this.fpInstance = flatpickr(".daterange", {
      enableTime: true,
      time_24hr: true,
      minuteIncrement: 5,
      dateFormat: "Y-m-d H:i",
      mode: "range",
      onReady: (selectedDates, dateStr, instance) => {
        // Контейнер для кнопок
        const btnContainer = document.createElement("div");
        btnContainer.className = "fp-quick-range-buttons d-flex gap-2 p-2";

        // Диапазоны
        const ranges = [
          { label: "Today", value: "today" },
          { label: "Yesterday", value: "yesterday" },
          { label: "This Month", value: "thisMonth" },
          { label: "Last Month", value: "lastMonth" }
        ];

        ranges.forEach(r => {
          const btn = document.createElement("button");
          btn.type = "button";
          btn.textContent = r.label;
          btn.className = "btn btn-sm btn-outline-primary"; // Bootstrap стиль
          btn.style.padding = "2px 6px"; // меньше, чем у btn-sm по умолчанию
          btn.style.fontSize = "12px";   // чуть меньше шрифт
          btn.addEventListener("click", () => this.setQuickRange(r.value));
          btnContainer.appendChild(btn);
        });
        instance.calendarContainer.appendChild(btnContainer);
      },
      
      onClose: (selectedDates) => {
        if (selectedDates.length === 2) {
          this.filters.date_from = selectedDates[0].toISOString().slice(0, 19).replace('T', ' ');
          this.filters.date_to   = selectedDates[1].toISOString().slice(0, 19).replace('T', ' ');
        } else {
          this.filters.date_from = "";
          this.filters.date_to = "";
        }
        this.currentPage = 1;
        this.fetchData();
      }
    });
  },
  beforeUnmount() {
    this.removeTextFilterClickOutside();
    this.removeStatusFilterClickOutside();
    if (this.fpInstance) {
      this.fpInstance.destroy();
      this.fpInstance = null;
    }
  },
  methods: {
    isFilterActive(field) {
      return (this.filters[field] || "").toString().trim() !== "";
    },
    setPopoverPosition(buttonRef, width, styleKey) {
      const btn = this.$refs[buttonRef];
      if (!btn) return;

      const rect = btn.getBoundingClientRect();
      let top = rect.bottom + 8;
      let left = rect.left;

      if (left + width > window.innerWidth) {
        left = Math.max(8, window.innerWidth - width - 8);
      }

      this[styleKey] = {
        top: `${top}px`,
        left: `${left}px`
      };
    },
    toggleTextFilterPopover(field, title, buttonRef) {
      if (this.showTextFilterModal && this.activeTextFilterField === field) {
        this.closeTextFilterModal();
        return;
      }

      this.closeStatusFilterModal();
      this.activeTextFilterField = field;
      this.activeTextFilterTitle = title;
      this.activeTextFilterButtonRef = buttonRef;
      this.textFilterTemp = this.filters[field] || "";
      this.showTextFilterModal = true;

      this.$nextTick(() => {
        this.setPopoverPosition(buttonRef, 280, "textFilterPopoverStyle");
        this.addTextFilterClickOutside();
      });
    },
    closeTextFilterModal() {
      this.showTextFilterModal = false;
      this.activeTextFilterField = '';
      this.activeTextFilterTitle = '';
      this.activeTextFilterButtonRef = '';
      this.textFilterTemp = '';
      this.removeTextFilterClickOutside();
    },
    applyTextFilter() {
      if (!this.activeTextFilterField) return;
      this.filters[this.activeTextFilterField] = (this.textFilterTemp || "").trim();
      this.currentPage = 1;
      this.closeTextFilterModal();
      this.fetchData();
    },
    clearTextFilter() {
      if (!this.activeTextFilterField) return;
      this.filters[this.activeTextFilterField] = "";
      this.currentPage = 1;
      this.closeTextFilterModal();
      this.fetchData();
    },
    addTextFilterClickOutside() {
      this.removeTextFilterClickOutside();
      this.textFilterClickOutside = (e) => {
        const btn = this.$refs[this.activeTextFilterButtonRef];
        const popover = this.$refs.textFilterPopover;
        if (btn && btn.contains(e.target)) return;
        if (popover && popover.contains(e.target)) return;
        this.closeTextFilterModal();
      };
      document.addEventListener("mousedown", this.textFilterClickOutside);
    },
    removeTextFilterClickOutside() {
      if (this.textFilterClickOutside) {
        document.removeEventListener("mousedown", this.textFilterClickOutside);
        this.textFilterClickOutside = null;
      }
    },
    toggleStatusFilterPopover() {
      if (this.showStatusFilterModal) {
        this.closeStatusFilterModal();
        return;
      }

      this.closeTextFilterModal();
      this.statusFilterTemp = this.filters.is_applied || "";
      this.showStatusFilterModal = true;

      this.$nextTick(() => {
        this.setPopoverPosition("statusFilterBtn", 320, "statusFilterPopoverStyle");
        this.addStatusFilterClickOutside();
      });
    },
    closeStatusFilterModal() {
      this.showStatusFilterModal = false;
      this.statusFilterTemp = '';
      this.removeStatusFilterClickOutside();
    },
    applyStatusFilter() {
      this.filters.is_applied = this.statusFilterTemp || "";
      this.currentPage = 1;
      this.closeStatusFilterModal();
      this.fetchData();
    },
    clearStatusFilter() {
      this.filters.is_applied = "";
      this.currentPage = 1;
      this.closeStatusFilterModal();
      this.fetchData();
    },
    addStatusFilterClickOutside() {
      this.removeStatusFilterClickOutside();
      this.statusFilterClickOutside = (e) => {
        const btn = this.$refs.statusFilterBtn;
        const popover = this.$refs.statusFilterPopover;
        if (btn && btn.contains(e.target)) return;
        if (popover && popover.contains(e.target)) return;
        this.closeStatusFilterModal();
      };
      document.addEventListener("mousedown", this.statusFilterClickOutside);
    },
    removeStatusFilterClickOutside() {
      if (this.statusFilterClickOutside) {
        document.removeEventListener("mousedown", this.statusFilterClickOutside);
        this.statusFilterClickOutside = null;
      }
    },
    async approveRefund(row) {
      if (row.is_applied) {
        return;
      }

      const token = localStorage.getItem("token");

      try {
        const res = await fetch(`/api/refund_requests/${row.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + token
          },
          body: JSON.stringify({
            is_applied: true
          })
        });

        if (!res.ok) {
          throw new Error("Failed to approve");
        }
        await fetch("/api/logs", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + localStorage.getItem("token")
          },
          body: JSON.stringify({
            action_type: `applied refund  ${row.id}`,
            username: localStorage.getItem("username")
          })
        });

        row.is_applied = true;

      } catch (e) {
        console.error("Approval error:", e);
      }
    },
    async openRefundById(id) {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/refund_requests/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) return;
        const row = await res.json();
        this.openRefundModal(row);
      } catch (e) {
        console.error('Failed to open refund by id:', e);
      }
    },

    showReasonOnly(reason) {
      this.reasonPopoverText = reason || '';
      this.showReasonPopover = true;
    },
    closeReasonPopover() {
      this.showReasonPopover = false;
      this.reasonPopoverText = '';
    },
    openRefundModal(row) {
      this.selectedRefundRow = row;
      this.modalAudioPlaying = false;
      this.showRefundModal = true;
    },

    closeRefundModal() {
      const audio = document.getElementById('modal-audio');
      if (audio) { audio.pause(); }
      this.modalAudioPlaying = false;
      this.showRefundModal = false;
      this.selectedRefund = null;
    },

    truncateUrl(url) {
      if (!url) return '';
      try {
        const u = new URL(url);
        return u.origin + u.pathname.substring(0, 30) + '...';
      } catch {
        return url.substring(0, 40) + '...';
      }
    },

    toggleModalAudio() {
      const audio = document.getElementById('modal-audio');
      if (!audio) return;
      if (this.modalAudioPlaying) {
        audio.pause();
        this.modalAudioPlaying = false;
      } else {
        audio.play().catch(() => { this.modalAudioPlaying = false; });
        this.modalAudioPlaying = true;
      }
    },

    async downloadModalRecording() {
      const url = this.selectedRefundRow.recording_url;
      if (!url) return;
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/proxyDownload?url=${encodeURIComponent(url)}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!res.ok) throw new Error('Download failed');
        const blob = await res.blob();
        const fileName = url.split('/').pop().split('?')[0] || 'recording.mp3';
        const link = document.createElement('a');
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(link.href);
      } catch (e) {
        console.error('Download error:', e);
      }
    },

    async makeDecision(status) {
      const token = localStorage.getItem('token');
      try {
        const res = await fetch(`/api/refund_requests/${this.selectedRefundRow.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ is_applied: status })
        });
        if (!res.ok) throw new Error('Failed to update');

        const rowIndex = this.rows.findIndex(r => r.id === this.selectedRefundRow.id);
        if (rowIndex !== -1) {
          this.rows[rowIndex] = { ...this.rows[rowIndex], is_applied: status };
        }
        this.selectedRefundRow = { ...this.selectedRefundRow, is_applied: status };
        this.pendingRefundCount = Math.max(0, this.pendingRefundCount - 1);
      } catch (e) {
        console.error('Decision error:', e);
      }
    },
    async updateRefundStatus(row) {
      const token = localStorage.getItem("token");
      try {
        const res = await fetch(`/api/refund_requests/${row.id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + token
          },
          body: JSON.stringify({
            is_applied: row.is_applied
          })
        });

        if (!res.ok) throw new Error("Failed to update refund status");

        await fetch("/api/logs", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + token
          },
          body: JSON.stringify({
            action_type: `updated refund ${row.id} status to ${row.is_applied}`,
            username: localStorage.getItem("username")
          })
        });

        // Закрываем режим редактирования после сохранения
        this.editingStatusId = null;
      } catch (e) {
        console.error("Update error:", e);
        this.editingStatusId = null;
      }
    },
    onTopDateChange(value) {
    if (value && value.length === 2) {
      this.filters.date_from = value[0].toISOString().slice(0, 19).replace('T', ' ');
      this.filters.date_to = value[1].toISOString().slice(0, 19).replace('T', ' ');
    } else {
      this.filters.date_from = "";
      this.filters.date_to = "";
    }
    this.currentPage = 1;
    this.fetchData();
  },
    setQuickRange(range) {
      const today = new Date();
      let from = null;
      let to = null;

      switch (range) {
        case 'today':
          from = new Date(today.setHours(0, 0, 0, 0));
          to = new Date(today.setHours(23, 59, 59, 999));
          break;
        case 'yesterday':
          from = new Date(today.setDate(today.getDate() - 1));
          from.setHours(0, 0, 0, 0);
          to = new Date(today.setDate(today.getDate() - 0));
          to.setHours(23, 59, 59, 999);
          break;
        case 'thisMonth':
          from = new Date(today.getFullYear(), today.getMonth(), 1);
          to = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);
          break;
        case 'lastMonth':
          from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          to = new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59, 999);
          break;
      }

      if (from && to) {
        this.filters.date_from = from.toISOString().slice(0, 19).replace('T', ' ');
        this.filters.date_to = to.toISOString().slice(0, 19).replace('T', ' ');
        
        if (this.fpInstance) {
          this.fpInstance.setDate([from, to], true);
        }

        this.currentPage = 1;
        this.fetchData();
      }
    },
    changePage(page) {
      if (page < 1 || page > this.totalPages) return;
      this.currentPage = page;
      this.fetchData();
    },
    async check_role(){
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ user: this.userName });
      const res = await fetch(`/api/role?${params.toString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      this.roles = data.roles;
      return this.roles
    },
    async fetchData() {
      const token = localStorage.getItem('token');
      this.isLoading = true;
      await this.$nextTick();
      const cleanFilters = {};
      Object.keys(this.filters).forEach(key => {
        cleanFilters[key] = this.filters[key] == null ? '' : this.filters[key];
      });
      const params = new URLSearchParams({
        page: this.currentPage,
        page_size: this.pageSize,
        ...cleanFilters
      });
      try {
        const res = await fetch(`/api/refund_requests?${params.toString()}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (res.status === 401) {
          localStorage.removeItem('token');
          this.$router.push('/login');
          return;
        }
        const data = await res.json();
        this.rows = data.items || [];
        this.totalItems = Number(data.total || 0);
        this.totalPages = Math.max(1, Math.ceil(this.totalItems / this.pageSize));
        return data;
      } finally {
        this.isLoading = false;
      }
    },
    clearFilters() {
      this.filters = {
        inbound_phone: "",
        call_dt: "",
        call_length: "",
        end_call_source: "",
        target_name: "",
        target_number: "",
        reason: "",
        is_applied: "",
        created_date: "",
        user: "",
        date_from: "",
        date_to: ""
      };
      this.currentPage = 1;
      this.closeTextFilterModal();
      this.closeStatusFilterModal();
      if (this.fpInstance) {
        this.fpInstance.clear(false);
      }
      this.fetchData();
    },
    async downloadCSV() {
      if (this.isExportingCsv) return;

      try {
        this.isExportingCsv = true;
        const cleanFilters = {};
        Object.keys(this.filters).forEach(key => {
          cleanFilters[key] = this.filters[key] == null ? '' : this.filters[key];
        });
        const params = new URLSearchParams(cleanFilters);

        await fetch("/api/logs", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": "Bearer " + localStorage.getItem("token")
          },
          body: JSON.stringify({
            action_type: "download_refund_requests_csv",
            username: localStorage.getItem("username")
          })
        });

        const res = await fetch(`/api/refund_requests/csv?${params.toString()}`, {
          headers: { "Authorization": "Bearer " + localStorage.getItem("token") }
        });

        if (!res.ok) throw new Error("CSV uploading error");

        const blob = await res.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = "refund_requests.csv";
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (e) {
        console.error("Ошибка скачивания CSV:", e);
        alert(`Failed to download CSV: ${e.message || e}`);
      } finally {
        this.isExportingCsv = false;
      }
    },
  }
};
</script>

<style scoped>

.daterange {
  cursor: pointer;
  background-color: #fff;
}
#reportPage {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: linear-gradient(135deg, #1e3c72, #2a5298);
  padding: 20px 10px;
}

.loading-overlay {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(15, 23, 42, 0.32);
  backdrop-filter: blur(2px);
  z-index: 10000;
}

.loading-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 18px 22px;
  background: rgba(255, 255, 255, 0.92);
  border-radius: 14px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
}

.loading-spinner {
  width: 52px;
  height: 52px;
  border: 4px solid rgba(78, 84, 200, 0.18);
  border-top-color: #4e54c8;
  border-radius: 50%;
  animation: refund-spin 0.8s linear infinite;
}

.loading-text {
  font-size: 14px;
  font-weight: 600;
  color: #334155;
}

@keyframes refund-spin {
  to {
    transform: rotate(360deg);
  }
}

#reportPage main {
  display: flex;
  justify-content: center;
  width: 100%;
}

#reportPage article {
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: none;
}

.title-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
}

.login-title {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: #4e54c8;
}

.campaign-header-cell {
  vertical-align: bottom !important;
}

.campaign-header-cell-inner {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.campaign-header-cell span {
  font-weight: 600;
}

.filter-icon-btn {
  position: relative;
  width: auto;
  height: auto;
  padding: 2px 4px;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: #6c757d;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: color 0.2s;
}

.filter-icon-btn:hover {
  color: #4e54c8;
}

.filter-icon-btn.active {
  color: #4e54c8;
}

.filter-badge {
  position: absolute;
  top: -6px;
  right: -6px;
  min-width: 14px;
  height: 14px;
  padding: 0 3px;
  font-size: 9px;
  font-weight: 700;
  color: #fff;
  background: #4e54c8;
  border-radius: 7px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
}

.filter-popover {
  position: fixed;
  z-index: 9999;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  border: 1px solid #e0e0e0;
  width: 320px;
  max-height: 400px;
  display: flex;
  flex-direction: column;
}

.caller-filter-popover.filter-popover {
  width: 280px;
  max-height: none;
}

.filter-popover-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 12px;
  border-bottom: 1px solid #eee;
}

.filter-popover-title {
  font-size: 13px;
  font-weight: 600;
  color: #1a1a2e;
}

.filter-popover-close {
  width: 24px;
  height: 24px;
  padding: 0;
  border: none;
  background: none;
  font-size: 18px;
  color: #888;
  cursor: pointer;
  line-height: 1;
}

.filter-popover-close:hover {
  color: #333;
}

.filter-popover-body {
  padding: 10px 12px;
  overflow-y: auto;
  flex: 1;
}

.filter-popover-search {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #ced4da;
  border-radius: 6px;
  font-size: 13px;
  margin-bottom: 8px;
}

.filter-popover-search:focus {
  outline: none;
  border-color: #4e54c8;
}

.filter-popover-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  cursor: pointer;
  border-bottom: 1px solid #f5f5f5;
  margin: 0;
  font-size: 12px;
}

.filter-popover-item:last-child {
  border-bottom: none;
}

.filter-popover-item:hover {
  background: #f8f9ff;
}

.filter-popover-footer {
  display: flex;
  justify-content: flex-end;
  gap: 6px;
  padding: 10px 12px;
  border-top: 1px solid #eee;
}

.panel-action-btn {
  background: linear-gradient(135deg, #6a11cb, #2575fc);
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  font-size: 16px;
  padding: 10px 20px;
  cursor: pointer;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-width: 150px;
  max-width: 150px;
  height: 106.59px;
  flex-shrink: 0;
  margin-left: auto;
}

.panel-action-btn:hover {
  transform: translateY(-2px);
}

.panel-action-btn:active {
  transform: translateY(1px);
}

.btn-submit {
  width: 200px;
  max-width: 100%;
  display: inline-block;
  background: linear-gradient(135deg, #6a11cb, #2575fc);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease, transform 0.2s ease;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.btn-submit:hover {
  background: linear-gradient(135deg, #5a0fc1, #1e63d6);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
}

.btn-submit:active {
  transform: translateY(1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.summary-panels-wrapper {
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  gap: 30px;
}


.summary-panels {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  flex: 1;
}

.datepicker-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.datepicker-wrapper .calendar-icon {
  position: absolute;
  right: 10px;
  color: #4e54c8;
  cursor: pointer;
}

.datepicker-wrapper input {
  padding-right: 30px; /* чтобы иконка не перекрывала текст */
  border-radius: 12px;
  border: 1px solid #ccc;
  font-size: 12px;
  height: 32px;
}

.datepicker-wrapper input:focus {
  border-color: #4e54c8;
  box-shadow: 0 0 0 2px rgba(78, 84, 200, 0.2);
}


.panel {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 110px;
  background: #f0f4ff;
  padding: 10px 15px;
  border-radius: 8px;
  text-align: center;
  flex: 1 1 150px;
  min-width: 150px;
  max-width: 150px;
}

.panel-title {
  font-weight: 600;
  font-size: 20px;
  line-height: 24px;
}

.panel-value {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
}
.v-select {
  font-size: 12px;
  border-radius: 8px;
}

.v-select .vs__dropdown-toggle {
  min-height: 32px;
}

.v-select .vs__selected {
  font-size: 12px;
}


.table-responsive table {
  min-width: 700px;
  -webkit-overflow-scrolling: touch;
  table-layout: fixed; /* фиксированная ширина столбцов */
  width: 100%
}

table {
  width: 100%;
  table-layout: auto;
  font-size: 10px;
}

th, td {
  word-wrap: break-word;
  vertical-align: middle !important;
}

.filter-input,
.v-select .vs__dropdown-toggle {
  height: 32px; /* или match с input */
  min-height: 32px;
  font-size: 10px;
  padding: 6px 8px;
}

.multiselect-sm {
  font-size: 10px;
}

.multiselect-sm .vs__dropdown-toggle {
  min-height: 32px;      /* как у form-control-sm */
  height: 32px;
  font-size: 10px;
  border-radius: 8px;    /* можно match bootstrap */
  border: 1px solid #ced4da;
  padding: 0 6px;
  background-color: #fff;
}

.multiselect-sm .vs__selected {
  font-size: 10px;
  line-height: 32px;     /* выравнивание по центру */
  padding: 0 4px;
}

/* placeholder */
.multiselect-sm .vs__placeholder {
  font-size: 10px;
  line-height: 32px;
  color: #6c757d;        /* как placeholder bootstrap */
}

/* выпадающий список */
.multiselect-sm .vs__dropdown-menu {
  font-size: 10px;
}

/* input внутри multiselect */
.multiselect-sm .vs__search {
  font-size: 10px;
  line-height: 32px;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;  /* Горизонтальная прокрутка */
  -webkit-overflow-scrolling: touch; /* Плавная прокрутка на iOS */
}

.table-responsive table {
  min-width: 1000px; /* Минимальная ширина таблицы для всех колонок */
  width: 100%;
  table-layout: auto; /* Авто ширина столбцов */
}

@media (max-width: 768px) {
  .table-responsive table {
    min-width: 700px; /* Если экран узкий, оставляем минимальную ширину */
  }
  th, td {
    white-space: nowrap; /* Запрещаем перенос текста, чтобы не ломало строки */
  }
}
.multiselect-sm .vs__dropdown-toggle,
.multiselect-sm .vs__actions,
.multiselect-sm .vs__search {
  border-radius: 10px; /* совпадает с остальными инпутами */
}


/* Адаптив */
@media (max-width: 768px) {
  .login-title { font-size: 22px; }
  table { font-size: 12px; }
  .filter-input { font-size: 11px; padding: 4px 6px; }
  .panel-action-btn { max-width: 100%; padding: 8px 16px; height: 110px; }
  .summary-panels-container { gap: 10px; }
}
.pie-chart-container {
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.1);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

.chart-title {
  margin-bottom: 15px;
  font-size: 18px;
  font-weight: 600;
  color: #4e54c8;
}

.summary-panels {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  flex-shrink: 0;
  align-items: stretch;
}

.panel {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: #f0f4ff;
  border-radius: 12px;
  padding: 15px;
  text-align: center;
  flex: 1 1 150px; /* минимальная ширина 150px, растягивается при необходимости */
  min-height: 150px;
}
.summary-panels-container {
  display: flex;
  justify-content: flex-start;
  align-items: stretch;
  gap: 20px;
}

.pie-chart-container.no-card {
  background: transparent; /* убираем белый фон */
  padding: 0;              /* убираем внутренние отступы */
  box-shadow: none;        /* убираем тень */
  border-radius: 0;        /* убираем скругление */
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}
.summary-panels-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 20px; /* увеличиваем отступ между панелями и графиком */
}
.quick-range-buttons {
  display: flex;
  gap: 5px;
  margin-top: 5px;
}


.quick-range-buttons button {
  padding: 3px 6px;
  font-size: 11px;
  border-radius: 5px;
  border: 1px solid #ccc;
  background: #f0f0f0;
  cursor: pointer;
}

.quick-range-buttons button:hover {
  background: #e0e0ff;
}

.fp-quick-range-buttons {
  display: flex;
  gap: 8px;
  margin-top: 10px;
  padding: 5px 10px;
  border-top: none;
  justify-content: flex-start;
}

.fp-quick-range-buttons .fp-quick-btn {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  border-radius: 10px;
  border: none;
  background: linear-gradient(135deg, #6a11cb, #2575fc);
  color: #fff;
  cursor: pointer;
  box-shadow: 0 3px 8px rgba(0,0,0,0.15);
  transition: all 0.3s ease;
}

.fp-quick-range-buttons .fp-quick-btn:hover {
  background: linear-gradient(135deg, #5a0fc1, #1e63d6);
  transform: translateY(-1px);
  box-shadow: 0 5px 12px rgba(0,0,0,0.2);
}

.fp-quick-range-buttons .fp-quick-btn:active {
  transform: translateY(1px);
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}
.table-top-filters {
  display: flex;
  justify-content: flex-end; /* прижимает к правому краю таблицы */
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.link-csv {
  font-size: 12px;
  color: #4f46e5;
  text-decoration: none;
  display: flex;
  align-items: center;
  gap: 5px;
  white-space: nowrap;
}

.link-csv:hover {
  text-decoration: underline;
}

.link-csv--disabled {
  opacity: 0.6;
  pointer-events: none;
  cursor: wait;
}

.datepicker-top {
  max-width: 220px; /* чтобы не выходило за таблицу */
}

.quick-range-buttons button {
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 5px;
  border: 1px solid #ccc;
  background: #f0f0f0;
  cursor: pointer;
}

.quick-range-buttons button:hover {
  background: #e0e0ff;
}

.table-top-filters {
  position: relative; /* календарь позиционируется относительно контейнера */
  overflow: visible;  /* чтобы календарь не обрезался */
}
.refund-modal {
  max-width: 400px;
  padding: 20px;
}

.refund-modal textarea {
  font-size: 13px;
}
.daterange {
  cursor: pointer;
  background-color: #fff;
}
#reportPage {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  background: linear-gradient(135deg, #1e3c72, #2a5298);
  padding: 20px 10px;
}

#reportPage main {
  display: flex;
  justify-content: center;
  width: 100%;
}

#reportPage article {
  background: #fff;
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
  width: 100%;
  max-width: none;
}

.title-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  position: relative;
}

.login-title {
  margin: 0;
  font-size: 28px;
  font-weight: 600;
  color: #4e54c8;
}

.panel-action-btn {
  background: linear-gradient(135deg, #6a11cb, #2575fc);
  border: none;
  border-radius: 8px;
  color: white;
  font-weight: 600;
  font-size: 16px;
  padding: 10px 20px;
  cursor: pointer;
  display: inline-flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-width: 150px;
  max-width: 150px;
  height: 106.59px;
  flex-shrink: 0;
  margin-left: auto;
}

.panel-action-btn:hover {
  transform: translateY(-2px);
}

.panel-action-btn:active {
  transform: translateY(1px);
}

.btn-submit {
  width: 200px;
  max-width: 100%;
  display: inline-block;
  background: linear-gradient(135deg, #6a11cb, #2575fc);
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease, transform 0.2s ease;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

.btn-submit:hover {
  background: linear-gradient(135deg, #5a0fc1, #1e63d6);
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.25);
}

.btn-submit:active {
  transform: translateY(1px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
}

.summary-panels-wrapper {
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.summary-panels-container {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 20px;
  flex-wrap: wrap;
}

.summary-panels {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
  flex: 1;
}

.datepicker-wrapper {
  position: relative;
  display: flex;
  align-items: center;
}

.datepicker-wrapper .calendar-icon {
  position: absolute;
  right: 10px;
  color: #4e54c8;
  cursor: pointer;
}

.datepicker-wrapper input {
  padding-right: 30px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 12px;
  height: 32px;
}

.datepicker-wrapper input:focus {
  border-color: #4e54c8;
  box-shadow: 0 0 0 2px rgba(78, 84, 200, 0.2);
}


.panel {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 110px;
  background: #f0f4ff;
  padding: 10px 15px;
  border-radius: 8px;
  text-align: center;
  flex: 1 1 150px;
  min-width: 150px;
  max-width: 150px;
}

.panel-title {
  font-weight: 600;
  font-size: 20px;
  line-height: 24px;
}

.panel-value {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
}
.v-select {
  font-size: 12px;
  border-radius: 8px;
}

.v-select .vs__dropdown-toggle {
  min-height: 32px;
}

.v-select .vs__selected {
  font-size: 12px;
}


.table-responsive table {
  min-width: 700px;
  -webkit-overflow-scrolling: touch;
  table-layout: fixed;
  width: 100%
}

table {
  width: 100%;
  table-layout: auto;
  font-size: 12px;
}

th, td {
  word-wrap: break-word;
  vertical-align: middle !important;
}

.filter-input {
  font-size: 12px;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid #ccc;
  width: 100%;
}
.modal-overday {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
  }

/* Reason-only modal — same visual format as refund card */
.reason-modal-box {
  background: #fff;
  border-radius: 14px;
  width: 90%;
  max-width: 560px;
  min-height: 280px;
  display: flex;
  flex-direction: column;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: scaleIn 0.2s ease;
}
.reason-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px 14px;
  background: linear-gradient(135deg, #1e3c72, #2a5298);
}
.reason-modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #fff;
  letter-spacing: -0.02em;
}
.reason-modal-close {
  background: rgba(255, 255, 255, 0.15);
  border: none;
  color: #fff;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
  line-height: 1;
}
.reason-modal-close:hover {
  background: rgba(255, 255, 255, 0.3);
}
.reason-modal-body {
  flex: 1;
  padding: 24px 24px;
  display: flex;
  align-items: flex-start;
  min-height: 160px;
}
.reason-modal-text {
  margin: 0;
  font-size: 15px;
  line-height: 1.6;
  color: #111827;
  white-space: pre-wrap;
  word-break: break-word;
  font-weight: 500;
}
.reason-modal-footer {
  padding: 14px 24px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
  background: #fafafa;
}

.refund-reason-chip-clickable {
  cursor: pointer;
}
.refund-reason-chip-clickable:hover {
  opacity: 0.9;
}

.request-id-link {
  color: #1e3c72;
  font-weight: 600;
  text-decoration: none;
  font-size: 13px;
  white-space: nowrap;
}
.request-id-link:hover {
  text-decoration: underline;
  color: #2a5298;
}

/* First column (Request #) — keep on one line, fixed width */
.table-responsive table th:first-child,
.table-responsive table td:first-child {
  min-width: 100px;
  white-space: nowrap;
}

.btn {
  padding: 6px 14px;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-weight: 300;
}

.btn-primary {
  background: #4f46e5;
  color: white;
  font-size: 12px;
}

.table-responsive {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  font-size: 10px;
}

.table-responsive table {
  min-width: 1000px;
  width: 100%;
  table-layout: auto;
}

@media (max-width: 768px) {
  .table-responsive table {
    min-width: 700px;
  }
  th, td {
    white-space: nowrap;
  }
}
@media (max-width: 768px) {
  .login-title { font-size: 22px; }
  table { font-size: 12px; }
  .filter-input { font-size: 11px; padding: 4px 6px; }
  .panel-action-btn { max-width: 100%; padding: 8px 16px; height: 110px; }
  .summary-panels-container { gap: 10px; }
}

.large-textarea {
  width: 100%;
  min-height: 150px;
  padding: 12px;
  border-radius: 8px;
  resize: vertical;
  font-size: 14px;
}

.refund-modal-content {
  background: #fff;
  border-radius: 14px;
  width: 90%;
  max-width: 560px;
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  animation: scaleIn 0.2s ease;
}

.refund-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 24px 14px;
  border-bottom: 1px solid #f0f0f0;
  background: linear-gradient(135deg, #1e3c72, #2a5298);
}

.refund-modal-title-block {
  display: flex;
  align-items: center;
}

.refund-modal-id {
  font-size: 20px;
  font-weight: 700;
  color: #fff;
}

.refund-modal-close {
  background: rgba(255,255,255,0.15);
  border: none;
  color: #fff;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 14px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.refund-modal-close:hover {
  background: rgba(255,255,255,0.3);
}

.refund-modal-body {
  padding: 20px 24px;
}

.refund-info-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 20px;
  margin-bottom: 20px;
}

.refund-info-item {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.refund-info-label {
  font-size: 11px;
  font-weight: 600;
  color: #9ca3af;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.refund-info-value {
  font-size: 13px;
  color: #111827;
  font-weight: 500;
}

.refund-reason-block {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.refund-reason-block-full {
  margin-top: 4px;
}
.refund-reason-full-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: #111827;
  white-space: pre-wrap;
  word-break: break-word;
  font-weight: 500;
  padding: 10px 12px;
  background: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.refund-reason-block .refund-reason-chip {
  cursor: default;
}

.refund-reason-chip {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  width: 150px;
  min-width: 150px;
  flex-shrink: 0;
  padding: 3px 8px;
  border-radius: 6px;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  border: 1px solid transparent;
  transition: opacity 0.15s;
  line-height: 1.4;
}

.refund-reason-chip:hover {
  opacity: 0.8;
}

.refund-chip-text {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.refund-chip-status-dot {
  flex-shrink: 0;
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.refund-chip-pending {
  background: #f3f4f6;
  color: #374151;
  border-color: #d1d5db;
}

.refund-chip-pending .refund-chip-status-dot {
  background: #9ca3af;
}

.refund-chip-approved {
  background: #dcfce7;
  color: #166534;
  border-color: #bbf7d0;
}

.refund-chip-approved .refund-chip-status-dot {
  background: #16a34a;
}

.refund-chip-rejected {
  background: #fee2e2;
  color: #991b1b;
  border-color: #fecaca;
}

.refund-chip-rejected .refund-chip-status-dot {
  background: #dc2626;
}

/* Recording block */
.refund-recording-block {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 4px;
}

.refund-recording-controls {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 14px;
}

.audio-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  font-size: 12px;
  flex-shrink: 0;
  transition: background 0.15s, transform 0.1s;
}

.audio-btn:active { transform: scale(0.92); }

.audio-btn.play-btn {
  background: #2563eb;
  color: #fff;
}

.audio-btn.play-btn:hover {
  background: #1d4ed8;
}

.audio-btn.download-btn {
  background: #e0f2fe;
  color: #0369a1;
  text-decoration: none;
}

.audio-btn.download-btn:hover {
  background: #bae6fd;
}

.refund-recording-url {
  flex: 1;
  font-size: 11px;
  color: #6b7280;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* Decision block */
.refund-modal-decision {
  margin: 0 24px 16px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
}

.refund-decision-buttons {
  display: flex;
  gap: 10px;
}

.btn-decision {
  flex: 1;
  padding: 9px 16px;
  border-radius: 8px;
  border: none;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  transition: background 0.15s, transform 0.1s;
}

.btn-decision:active { transform: scale(0.97); }

.btn-decision.approve {
  background: #dcfce7;
  color: #166534;
}

.btn-decision.approve:hover {
  background: #bbf7d0;
}

.btn-decision.reject {
  background: #fee2e2;
  color: #991b1b;
}

.btn-decision.reject:hover {
  background: #fecaca;
}

.refund-modal-footer {
  padding: 14px 24px;
  border-top: 1px solid #f0f0f0;
  display: flex;
  justify-content: flex-end;
}

@keyframes scaleIn {
  from { opacity: 0; transform: scale(0.95); }
  to   { opacity: 1; transform: scale(1); }
}
.btn-secondary {
  background: #e5e7eb;
  color: #111;
}

.btn-secondary:hover {
  background: #d1d5db;
}
.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;              /* расстояние между кнопками */
}

.modal-actions .btn {
  min-width: 100px;       /* одинаковая ширина */
  height: 40px;           /* одинаковая высота */
  padding: 0 16px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.table thead {
  font-size: 10px;
  font-weight: 600;
}

.table tbody {
  font-size: 10px;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

.status-badge .dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

/* APPROVED - зеленый */
.status-badge.approved {
  background: #ecfdf5;
  color: #065f46;
}

.status-badge.approved .dot {
  background: #10b981;
}

/* REJECTED - красный */
.status-badge.rejected {
  background: #fef2f2;
  color: #7f1d1d;
}

.status-badge.rejected .dot {
  background: #ef4444;
}

/* N/A - серый */
.status-badge.na {
  background: #f3f4f6;
  color: #4b5563;
}

.status-badge.na .dot {
  background: #9ca3af;
}

/* Бейджи внутри тёмного хедера модалки */
.refund-modal-header .status-badge.approved {
  background: rgba(16, 185, 129, 0.25);
  color: #6ee7b7;
}
.refund-modal-header .status-badge.approved .dot {
  background: #6ee7b7;
}
.refund-modal-header .status-badge.rejected {
  background: rgba(239, 68, 68, 0.25);
  color: #fca5a5;
}
.refund-modal-header .status-badge.rejected .dot {
  background: #fca5a5;
}
.refund-modal-header .status-badge.na {
  background: rgba(255, 255, 255, 0.15);
  color: #e5e7eb;
}
.refund-modal-header .status-badge.na .dot {
  background: #d1d5db;
}

/* Кликабельный badge для админов */
.status-badge.clickable {
  transition: all 0.2s ease;
}

.status-badge.clickable:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.tooltip-container {
  position: relative;
  display: inline-block;
  cursor: default;
}

.tooltip-container .tooltip-text {
  visibility: hidden;
  width: max-content;
  background-color: #333;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 4px 8px;
  position: absolute;
  z-index: 1;
  opacity: 0;
  transition: opacity 0.2s;
}

.tooltip-container:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}

.tooltip-container .tooltip-text.right {
  top: 50%;
  left: 100%;
  transform: translate(8px, -50%);
}

</style>